import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components';
import { useReportSasUrl } from '@/hooks/useProApi';

/**
 * Height that runs from wherever the element starts to the bottom of the window. The viewer was a
 * fixed 75vh, which left a dead band under the PDF on a tall window and cut it short on a small one.
 */
function useFillToBottom(gap = 36, min = 420) {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>(min);
  useEffect(() => {
    const measure = () => {
      if (!ref.current) return;
      const top = ref.current.getBoundingClientRect().top;
      setHeight(Math.max(min, Math.floor(window.innerHeight - top - gap)));
    };
    measure();
    window.addEventListener('resize', measure);
    const t = window.setTimeout(measure, 300); // after the page header has laid out
    return () => { window.removeEventListener('resize', measure); window.clearTimeout(t); };
  }, [gap, min]);
  return { ref, height };
}

export function ReportViewer() {
  const fill = useFillToBottom();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useReportSasUrl(id);

  return (
    <div className="space-y-4">
      {/* Back link — goes back to the report generator / previous page */}
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center text-gray-400 hover:text-white transition"
      >
        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Report Generator
      </button>

      {/* Toolbar */}
      {data && (
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-white truncate">
            {data.fileName?.replace('.pdf', '') || 'Report'}
          </h1>
          <div className="flex items-center gap-3">
            <button
              onClick={() => window.open(data.downloadUrl, '_blank')}
              className="flex items-center gap-2 px-4 py-2 bg-obdai-deep text-gray-300 rounded-lg hover:bg-white/10 transition text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download
            </button>
            <button
              onClick={() => {
                const w = window.open(data.downloadUrl, '_blank');
                w?.addEventListener('load', () => w.print());
              }}
              className="flex items-center gap-2 px-4 py-2 bg-obdai-deep text-gray-300 rounded-lg hover:bg-white/10 transition text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print
            </button>
          </div>
        </div>
      )}

      {/* PDF Display */}
      {isLoading ? (
        <div ref={fill.ref} className="flex items-center justify-center" style={{ height: fill.height }}>
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-obdai-gradient-start mx-auto mb-4" />
            <p className="text-gray-400">Loading report...</p>
          </div>
        </div>
      ) : error ? (
        <Card>
          <div className="text-center py-12">
            <p className="text-red-400 mb-4">Failed to load report</p>
            <button
              onClick={() => refetch()}
              className="px-4 py-2 bg-gradient-to-r from-[#6d7cff] to-[#e866dd] text-white rounded-lg text-sm"
            >
              Retry
            </button>
          </div>
        </Card>
      ) : data ? (
        <div ref={fill.ref} className="rounded-xl overflow-hidden border border-gray-700/50">
          <iframe
            src={data.downloadUrl}
            className="w-full bg-white block"
            style={{ height: fill.height }}
            title="PDF Report"
          />
        </div>
      ) : null}
    </div>
  );
}
